import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { fetchOwnAppointmentCalendarFile } from '@/api/appointments/appointmentsApi';
import type { OwnerService } from '@/api/services/servicesApi.types';
import { useOwnServicesQuery } from '@/hooks/useOwnServicesQuery';
import { saveBlobAsFile } from '@/utils/fileDownload';
import { formatPriceFromMinorUnits } from '@/utils/moneyFormatting';
import { showErrorNotification } from '@/utils/notifications';

interface UseVisitCalendarAndPricingResult {
  formatVisitPrice: (therapyType: string) => string | null;
  downloadVisitCalendar: (visitId: string) => void;
}

export function useVisitCalendarAndPricing(): UseVisitCalendarAndPricingResult {
  const { t, i18n } = useTranslation('clients');
  const servicesQuery = useOwnServicesQuery(true);

  const servicePriceByTherapyType = useMemo(
    function buildLookup() {
      const lookup: Record<string, OwnerService> = {};
      const loadedServices = servicesQuery.data === undefined ? [] : servicesQuery.data;
      loadedServices.forEach(function index(service) {
        if (service.therapyType !== null) {
          lookup[service.therapyType] = service;
        }
      });
      return lookup;
    },
    [servicesQuery.data]
  );

  function formatVisitPrice(therapyType: string): string | null {
    const matchedService = servicePriceByTherapyType[therapyType];
    if (matchedService === undefined) {
      return null;
    }
    const formatted = formatPriceFromMinorUnits(
      matchedService.priceMinorUnits,
      matchedService.currency,
      i18n.language
    );
    return formatted === null ? t('portal.priceOnRequest') : formatted;
  }

  async function downloadVisitCalendarFile(visitId: string) {
    try {
      const calendarFile = await fetchOwnAppointmentCalendarFile(visitId);
      saveBlobAsFile(calendarFile, 'appointment.ics');
    } catch (downloadError) {
      const backendMessage = resolveApiErrorMessage(downloadError);
      showErrorNotification(
        backendMessage.length > 0 ? backendMessage : t('portal.addToCalendarError')
      );
    }
  }

  function downloadVisitCalendar(visitId: string) {
    void downloadVisitCalendarFile(visitId);
  }

  return { formatVisitPrice, downloadVisitCalendar };
}
